import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { satelliteHeartbeatService } from "../services/satelliteHeartbeatService";
import { satelliteHeartbeatDisplayId } from "./SatelliteHeartbeatCommonStore";
import { SatelliteHeartbeat } from "../../../api/graphql";
import { satelliteMachineDisplayId } from "../../satelliteMachine/store/SatelliteMachineCommonStore";
import { satelliteMachineService } from "../../satelliteMachine/services/satelliteMachineService";
import { InitializeParams } from "../../../store/types";

export class SatelliteHeartbeatViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = satelliteHeartbeatService;
  satelliteHeartbeatInstance: SatelliteHeartbeat | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  receivedAtValue: Date | null = null;
  publicIpValue: string = "";
  latencyValue: number | undefined = undefined;
  cpuUsageValue: number | undefined = undefined;
  memoryUsageValue: number | undefined = undefined;
  diskUsageValue: number | undefined = undefined;
  activeTasksCountValue: number | undefined = undefined;
  proxyStatsValue: string = "{}";
  systemInfoValue: string = "{}";
  satelliteMachineService = satelliteMachineService;
  satelliteMachineSearchValue: string = "";
  satelliteMachineIdValue: string = "";
  satelliteMachineSelectedValue: { displayId: string; id: string } | null =
    null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadSatelliteHeartbeatById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.satelliteHeartbeatOne({ id });
    this.satelliteHeartbeatInstance = result.satelliteHeartbeatOne;
    if (this.satelliteHeartbeatInstance) {
      this.loadValues(this.satelliteHeartbeatInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.satelliteHeartbeatInstance) {
      return satelliteHeartbeatDisplayId(
        this.satelliteHeartbeatInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.SatelliteHeartbeatSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "satelliteMachine") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.satelliteMachineOne({
          id: params.returnData.resultId,
        });
        const satelliteMachine = result.satelliteMachineOne;
        this.satelliteMachineSelectedValue = satelliteMachine;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get satelliteMachineValue() {
    if (this.satelliteMachineSelectedValue) {
      return {
        id: this.satelliteMachineSelectedValue.id,
        displayId: satelliteMachineDisplayId(
          this.satelliteMachineSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.satelliteHeartbeatInstance = null;
    this.receivedAtValue = null;
    this.publicIpValue = "";
    this.latencyValue = undefined;
    this.cpuUsageValue = undefined;
    this.memoryUsageValue = undefined;
    this.diskUsageValue = undefined;
    this.activeTasksCountValue = undefined;
    this.proxyStatsValue = "{}";
    this.systemInfoValue = "{}";
    this.satelliteMachineSearchValue = "";
    this.satelliteMachineIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelSatelliteHeartbeat() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editSatelliteHeartbeat() {
    const id = this.satelliteHeartbeatInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/satelliteHeartbeat/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printSatelliteHeartbeat(handlePrint: () => void): void {
    handlePrint();
  }

  *closeSatelliteHeartbeat(): any {
    yield this.close();
  }

  loadValues(instance: SatelliteHeartbeat) {
    this.idValue = instance.id;
    this.receivedAtValue = new Date(instance.receivedAt);
    this.publicIpValue = instance.publicIp;
    this.latencyValue = instance.latency || undefined;
    this.cpuUsageValue = instance.cpuUsage || undefined;
    this.memoryUsageValue = instance.memoryUsage || undefined;
    this.diskUsageValue = instance.diskUsage || undefined;
    this.activeTasksCountValue = instance.activeTasksCount || undefined;
    this.proxyStatsValue = instance.proxyStats || "";
    this.systemInfoValue = instance.systemInfo || "";
    this.satelliteMachineSelectedValue = instance.satelliteMachine;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadSatelliteHeartbeatById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
